import { SITE_LAYER_GROUPS, SiteHazardKey, SiteLayerGroup } from './MapLayersAPI';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Loader2, Search, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MetricTheme, TOTAL_CATEGORY, getMetricCatalog } from './MetricsAPI';
import { BRIDGE_CATEGORIES, POWER_CATEGORIES, WAY_CATEGORIES } from './MapLayersAPI';
import { InkarGroup, InkarSelection, getInkarCatalog } from './InkarAPI';
import { POI_GROUPS, poiGroupLabel, poiTypeLabel } from './poiTypes';
import {
    option,
    optionBullet,
    optionGroup,
    optionLabel,
    optionList,
    optionListMetrics,
    optionMeta,
    optionSelected,
    sectionHint,
    subList,
    subOption,
    categoryList,
    optionMarked,
    optionChevron,
    clearAll as clearAllStyle,
} from './optionStyles';
import { categoryIcon, groupIcon } from './groupIcons';
import { CATEGORY_ORDER, CategoryKey, fixedCategory, inkarCategory, poiCategory, zensusCategory } from './categories';

/**
 * The metrics that can be drawn, read from the server's catalogue.
 *
 * Nothing is hard-coded: the catalogue lists the themes that actually have a loaded generation, so
 * a newly imported one appears here on its own. A breakdown theme opens to its categories — the
 * source's own column names — while a measure theme is a single entry.
 */

export interface MetricSelection {
    theme: string;
    themeLabel: string;
    category: string;
    categoryLabel: string;
}

const tidy = (value: string): string => value.replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Fold a word down to what a search should compare: the umlaut dropped to its
 * base letter, and punctuation gone.
 *
 * Punctuation because the catalogue's labels are full of dashes, slashes and
 * bracketed qualifiers that nobody types. The base letter because that is what a
 * reader in a hurry writes — "Flachennutzung" for "Flächennutzung" — and because
 * it also matches the umlaut itself, which folds to the same thing.
 */
const fold = (value: string): string =>
    tidy(
        value
            .toLowerCase()
            // Decompose, then drop the combining marks: ä → a, é → e. `ß` has no
            // decomposition and is spelled out.
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .replace(/ß/g, 'ss'),
    );

/**
 * The other way of writing an umlaut: "Flaechennutzung".
 *
 * Both spellings are in use and neither is the reader's mistake, so the entry is
 * searched under both — this one alongside the folded form, rather than the query
 * being rewritten. Rewriting the query would have to guess whether the "ae" in
 * what someone typed is an umlaut or two letters that happen to meet, and it
 * guesses wrong on words like "Israel".
 */
const spelledOut = (value: string): string =>
    tidy(value.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss'));

/**
 * Every word of the query has to appear somewhere in the entry — in any order,
 * as a prefix of a word or inside one. "wohn dichte" then finds
 * "Bevölkerungsdichte Wohnen" without the reader having to guess the label's
 * word order, which for a catalogue read from an import is not knowable.
 */
const matches = (terms: string[], ...labels: (string | undefined | null)[]): boolean => {
    if (terms.length === 0) return true;

    const haystack = labels
        .filter(Boolean)
        .flatMap((label) => {
            const folded = fold(label as string);
            const written = spelledOut(label as string);

            return written === folded ? [folded] : [folded, written];
        })
        .join(' ');

    return terms.every((term) => haystack.includes(term));
};

/** One thing that can be switched on next to whatever the metric colours. */
interface ToggleEntry {
    key: string;
    label: string;
    on: boolean;
    toggle: () => void;
}

/**
 * The row that opens a group: its mark, its name, and what it is doing.
 *
 * Bold whenever something inside is switched on — a closed group would otherwise
 * be indistinguishable from an empty one, and the reader who scrolled past it has
 * no way back to what they picked except opening every group in turn. The count
 * beside it says how much; the weight says *that* there is something, which is
 * the question being asked while scrolling.
 */
function GroupHeader({
    id,
    label,
    chosen,
    open,
    onOpen,
    meta,
}: {
    id: string;
    label: string;
    chosen: boolean;
    open: boolean;
    onOpen: (id: string | null) => void;
    /** The count of what is switched on, or a theme's year. */
    meta?: string | number;
}) {
    return (
        <button
            type="button"
            aria-expanded={open}
            className={cn(option, chosen && optionMarked)}
            onClick={() => onOpen(open ? null : id)}
        >
            {groupIcon(id)}
            <span className={optionLabel}>{label}</span>
            <span className={optionMeta}>{meta || ''}</span>
            {open ? (
                <ChevronDown size={13} strokeWidth={2} className={optionChevron} />
            ) : (
                <ChevronRight size={13} strokeWidth={2} className={optionChevron} />
            )}
        </button>
    );
}

/**
 * A group of additive switches: the surfaces, the power infrastructure, the
 * hazards, the points of interest.
 *
 * They all behave the same — open the group, tick what should be drawn. Only the
 * metrics differ, because there is exactly one of those.
 */
function ToggleGroup({
    id,
    label,
    entries,
    open,
    onOpen,
}: {
    id: string;
    label: string;
    entries: ToggleEntry[];
    open: boolean;
    onOpen: (id: string | null) => void;
}) {
    const chosen = entries.filter((entry) => entry.on).length;

    return (
        <div>
            <GroupHeader
                id={id}
                label={label}
                chosen={chosen > 0}
                meta={chosen > 0 ? chosen : ''}
                open={open}
                onOpen={onOpen}
            />

            {open ? (
                <div className={subList}>
                    {entries.map((entry) => (
                        <button
                            key={entry.key}
                            type="button"
                            aria-pressed={entry.on}
                            className={cn(subOption, entry.on && optionSelected)}
                            onClick={entry.toggle}
                        >
                            {entry.label}
                        </button>
                    ))}
                </div>
            ) : (
                <></>
            )}
        </div>
    );
}

interface Properties {
    selected: MetricSelection | null;
    onSelect: (selection: MetricSelection | null) => void;
    /**
     * The parcels are not a metric — they are an outline layer over whatever the metric colours.
     * They sit in this list because this is where a reader looks for what the map should show, but
     * they switch independently: a Zensus value under the cadastral grid is the useful picture.
     */
    parcelsShown: boolean;
    onToggleParcels: (shown: boolean) => void;
    /** Paint the parcels by Bodenrichtwert instead of as a neutral grid. */
    parcelsBrw: boolean;
    onToggleParcelsBrw: (shown: boolean) => void;
    /**
     * Land use, the same kind of thing as the parcels and for the same reason in
     * the same group: a surface over the metric, not a metric of its own.
     */
    brwZonesShown: boolean;
    onToggleBrwZones: (shown: boolean) => void;
    landUseShown: boolean;
    onToggleLandUse: (shown: boolean) => void;
    /**
     * Power supply infrastructure, per category — the grid, what generates,
     * what stores, what consumes. The same four the chat tool draws.
     */
    selectedPowerCategories: string[];
    onTogglePowerCategory: (key: string) => void;
    /**
     * The INKAR indicator drawn over the administrative units, if any.
     *
     * Exclusive with the Zensus metric the way a score is: both colour the map,
     * and two pictures at once is no picture. The caller is what enforces that —
     * this list only reports the choice.
     */
    inkarSelected: InkarSelection | null;
    onSelectInkar: (selection: InkarSelection | null) => void;
    /**
     * Roads and paths, per kind. Additive like the power categories and for the
     * same reason: nobody wants the footways and the motorways at once, and the
     * choice is what keeps the picture readable.
     */
    selectedWayCategories: string[];
    onToggleWayCategory: (key: string) => void;
    /** Bridges, per what crosses them. */
    selectedBridgeCategories: string[];
    onToggleBridgeCategory: (key: string) => void;
    /** Earthquakes: the historical record, circles sized by depth. */
    earthquakesShown: boolean;
    onToggleEarthquakes: (shown: boolean) => void;
    /** Schutzgebiete, Hochwasser (HWRM-RL), Starkregen — je ein Schalter neben den Erdbeben. */
    siteHazardsShown: SiteHazardKey[];
    onToggleSiteHazard: (key: SiteHazardKey) => void;
    /**
     * Points of interest, additive: a metric replaces, these accumulate. A site
     * is judged by charging points *and* schools, not by one of them.
     */
    selectedPoiTypes: string[];
    onTogglePoiType: (key: string) => void;
    /**
     * Switch everything in this list off at once.
     *
     * Undoing a selection one entry at a time means finding it again first, and
     * what is switched on is spread over groups that are closed by then. Only
     * this list's own entries — the score has its own section and its own way of
     * being let go.
     */
    onClearAll: () => void;
}

export default function MetricsSection({
    selected,
    onSelect,
    parcelsShown,
    onToggleParcels,
    parcelsBrw,
    onToggleParcelsBrw,
    brwZonesShown,
    onToggleBrwZones,
    landUseShown,
    onToggleLandUse,
    selectedPowerCategories,
    onTogglePowerCategory,
    selectedWayCategories,
    onToggleWayCategory,
    selectedBridgeCategories,
    onToggleBridgeCategory,
    inkarSelected,
    onSelectInkar,
    earthquakesShown,
    onToggleEarthquakes,
    siteHazardsShown,
    onToggleSiteHazard,
    selectedPoiTypes,
    onTogglePoiType,
    onClearAll,
}: Properties) {
    const { t, i18n } = useTranslation();
    const [metrics, setMetrics] = useState<MetricTheme[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [inkarGroups, setInkarGroups] = useState<InkarGroup[]>([]);
    const [openTheme, setOpenTheme] = useState<string | null>(null);
    /** Die aufgeklappte Kategorie — unabhängig von der Gruppe darin, damit beide offen sein können. */
    const [openCategory, setOpenCategory] = useState<CategoryKey | null>(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        let cancelled = false;

        getMetricCatalog()
            .then((catalog) => {
                if (!cancelled) setMetrics(catalog);
            })
            .catch((loadError: any) => {
                console.error('Could not load the metric catalogue', loadError);
                if (!cancelled) setError(loadError?.message ?? t('global.generalError'));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [t]);

    // The INKAR catalogue is derived from the model on the server and does not
    // change between two requests, so it is asked for once and kept.
    useEffect(() => {
        let cancelled = false;

        getInkarCatalog()
            .then((groups) => {
                if (!cancelled) setInkarGroups(groups);
            })
            .catch((loadError: any) => console.error('Could not load the INKAR catalogue', loadError));

        return () => {
            cancelled = true;
        };
    }, []);

    /**
     * Parcels and land use, in one group of their own.
     *
     * Both draw a surface the whole area is divided into, both add to whatever
     * the metric colours instead of replacing it, and both come from the
     * cadastre — so a reader looking for either looks in the same place. Loose at
     * the top of the list they read as a metric that is somehow always available,
     * which is what they are not.
     *
     * They are two answers to two questions, though, and switch independently:
     * the parcels are who owns the ground, the land use is what is on it. A plot
     * carries road, greenery and building at once; one use spans many plots. That
     * is why the land use has its own geometry here rather than being a colour on
     * the parcels, which is what it used to be.
     */
    const areaLayers: ToggleEntry[] = [
        {
            key: 'parcels',
            label: t('heatmap.parcels.label'),
            on: parcelsShown,
            toggle: () => onToggleParcels(!parcelsShown),
        },
        /**
         * How the parcels are painted, not whether they are drawn.
         *
         * A second switch rather than a third layer: it is the same outlines either way. Neutral,
         * they are a grid to read the tiles through at 6 % opacity; by Bodenrichtwert they are the
         * subject and cover what is underneath. Both are wanted, never at once.
         *
         * Only offered while the parcels are on — a switch that changes the look of something
         * invisible is a switch that does nothing.
         */
        ...(parcelsShown
            ? [
                  {
                      key: 'parcelsBrw',
                      label: t('heatmap.parcels.brwLabel'),
                      on: parcelsBrw,
                      toggle: () => onToggleParcelsBrw(!parcelsBrw),
                  },
              ]
            : []),
        /**
         * Die Bodenrichtwertzonen selbst, nicht die daraus gefärbten Flurstücke.
         *
         * Eigener Schalter und nicht eine dritte Stellung des Flurstücksschalters: es sind andere
         * Flächen. Gemessen über denselben 5-km-Ausschnitt hält Frankfurt 560 Zonen und 69.395
         * Flurstücke — das Flurstücksraster ist bei 1.500 gedeckelt und zeigt dort zwei Prozent,
         * die Zonenebene alles. Beide zusammen sind sinnvoll: die Zonen als Fläche, das Raster
         * als Gitter darüber.
         */
        {
            key: 'brwZones',
            label: t('heatmap.brwZones.label'),
            on: brwZonesShown,
            toggle: () => onToggleBrwZones(!brwZonesShown),
        },
        {
            key: 'landUse',
            label: t('heatmap.landUse.label'),
            on: landUseShown,
            toggle: () => onToggleLandUse(!landUseShown),
        },
    ];

    /**
     * Power supply infrastructure, one entry per category.
     *
     * Categories rather than a single switch because they answer different
     * questions and are rarely wanted together: whoever is looking for a grid
     * connection wants the substations and the lines, whoever is looking at the
     * energy mix wants what generates. Drawn together they are a mess of markers.
     */
    const powerLayers: ToggleEntry[] = POWER_CATEGORIES.map((category) => ({
        key: category,
        label: t(`heatmap.power.categories.${category}`),
        on: selectedPowerCategories.includes(category),
        toggle: () => onTogglePowerCategory(category),
    }));

    const wayLayers: ToggleEntry[] = WAY_CATEGORIES.map((category) => ({
        key: category,
        label: t(`heatmap.ways.categories.${category}`),
        on: selectedWayCategories.includes(category),
        toggle: () => onToggleWayCategory(category),
    }));

    const bridgeLayers: ToggleEntry[] = BRIDGE_CATEGORIES.map((category) => ({
        key: category,
        label: t(`heatmap.bridges.categories.${category}`),
        on: selectedBridgeCategories.includes(category),
        toggle: () => onToggleBridgeCategory(category),
    }));

    /** Die Schalter einer fachlichen Gruppe (SITE_LAYER_GROUPS). */
    const siteLayers = (group: SiteLayerGroup): ToggleEntry[] =>
        SITE_LAYER_GROUPS[group].map((key) => ({
            key,
            label: t(`heatmap.siteHazards.${key}.label`),
            on: siteHazardsShown.includes(key),
            toggle: () => onToggleSiteHazard(key),
        }));

    /** Naturgefahren: die Erdbeben-Ereignisse, dann Erdbebengefährdung, Hochwasser, Starkregen. */
    const hazardLayers: ToggleEntry[] = [
        {
            key: 'earthquakes',
            label: t('heatmap.earthquakes.label'),
            on: earthquakesShown,
            toggle: () => onToggleEarthquakes(!earthquakesShown),
        },
        ...siteLayers('hazards'),
    ];
    const climateLayers = siteLayers('climate');
    const natureLayers = siteLayers('nature');
    const planningLayers = siteLayers('planning');

    const layerGroup = (
        <ToggleGroup
            id="layers"
            label={t('heatmap.layers.label')}
            entries={areaLayers}
            open={openTheme === 'layers'}
            onOpen={setOpenTheme}
        />
    );

    // The search sits outside the scrolling list on purpose: inside it, typing would scroll the box
    // that holds the answer out of view.
    const searchBox = (
        <div className="relative mb-1.5">
            <Search
                size={14}
                strokeWidth={2}
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
                // A `search` field, for the semantics and the Escape key. Chrome draws its own clear
                // cross for those, which would sit next to ours — two identical buttons, one of
                // which the keyboard cannot reach.
                type="search"
                value={search}
                placeholder={t('heatmap.metrics.search')}
                aria-label={t('heatmap.metrics.search')}
                className="pl-8 pr-10 [&::-webkit-search-cancel-button]:appearance-none"
                onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    // Matches the field's own height so the control sits inside it, on touch too.
                    className="absolute right-0 top-1/2 h-9 w-9 -translate-y-1/2 text-muted-foreground max-sm:h-11 max-sm:w-11"
                    aria-label={t('global.clear')}
                    onClick={() => setSearch('')}
                >
                    <X size={14} strokeWidth={2} />
                </Button>
            ) : (
                <></>
            )}
        </div>
    );

    const language = i18n.language.startsWith('de') ? 'de' : 'en';

    /**
     * Pick an INKAR indicator, or let go of the one that is picked.
     *
     * Same rule as a metric: clicking the chosen one again clears it, because the
     * map draws one thing and the only other way back is to guess which entry was
     * responsible for what is on screen.
     */
    const pickInkar = (group: InkarGroup, indicator: { name: string; label: any }) => {
        const already = inkarSelected?.type === group.type && inkarSelected?.field === indicator.name;

        onSelectInkar(
            already
                ? null
                : {
                      type: group.type,
                      field: indicator.name,
                      label: indicator.label?.[language] ?? indicator.name,
                      groupLabel: group.label?.[language] ?? group.type,
                  },
        );
    };

    /** Everything this list has switched on, metric included. */
    const chosenCount =
        (selected ? 1 : 0) +
        (inkarSelected ? 1 : 0) +
        selectedWayCategories.length +
        selectedBridgeCategories.length +
        (parcelsShown ? 1 : 0) +
        (brwZonesShown ? 1 : 0) +
        (landUseShown ? 1 : 0) +
        selectedPowerCategories.length +
        (earthquakesShown ? 1 : 0) +
        siteHazardsShown.length +
        selectedPoiTypes.length;

    const clearAll =
        chosenCount > 0 ? (
            <button type="button" className={clearAllStyle} onClick={onClearAll}>
                <X size={12} strokeWidth={2} />
                {t('heatmap.metrics.clearAll', { count: chosenCount })}
            </button>
        ) : (
            <></>
        );

    const pick = (metric: MetricTheme, categoryKey: string, categoryLabel: string) => {
        const already = selected?.theme === metric.theme && selected?.category === categoryKey;
        onSelect(
            already
                ? null
                : {
                      theme: metric.theme,
                      themeLabel: metric.label,
                      category: categoryKey,
                      categoryLabel,
                  },
        );
    };

    /**
     * The state of the metric catalogue, which is the only part of this list that
     * has to be loaded.
     *
     * Everything else — the surfaces, the infrastructure, the hazards, the places
     * — is known without asking the server, so a slow or failing catalogue must
     * not take those groups off the screen with it. They are rendered once, after
     * this, and this stands in for the themes alone.
     */
    const catalogueState = loading ? (
        <span role="status" className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t('global.loading')} …
        </span>
    ) : error ? (
        <div className="text-sm text-destructive">{error}</div>
    ) : metrics.length === 0 ? (
        <div className={sectionHint}>{t('heatmap.metrics.none')}</div>
    ) : null;

    const terms = fold(search).split(' ').filter(Boolean);

    /**
     * What a search returns: the leaves, flat.
     *
     * A search that filtered the tree in place would leave the reader clicking
     * open the groups that survived — which is the work the search was meant to
     * save. So a hit is the thing itself, with the group it came from written
     * next to it: enough to tell two similarly named categories apart, and one
     * click to choose. The whole list is searched, metrics and points of interest
     * and surfaces alike, because a reader looking for "Schule" does not know
     * beforehand whether the answer is a metric or a marker.
     */
    const hits = terms.length > 0 ? searchHits() : [];

    function searchHits() {
        const found: any[] = [];

        /**
         * The group's own id travels with it, because the entry keys are not
         * unique on their own: `other` is a category of the power infrastructure,
         * of the ways *and* of the bridges, and `motorway` of the last two. Keyed
         * by the entry alone, three hits in the list claimed the same key.
         */
        const toggleGroups: [string, string, ToggleEntry[]][] = [
            ['layers', t('heatmap.layers.label'), areaLayers],
            ['power', t('heatmap.power.label'), powerLayers],
            ['ways', t('heatmap.ways.label'), wayLayers],
            ['bridges', t('heatmap.bridges.label'), bridgeLayers],
            ['hazards', t('heatmap.hazards.label'), hazardLayers],
            ['climate', t('heatmap.climate.label'), climateLayers],
            ['nature', t('heatmap.nature.label'), natureLayers],
            ['planning', t('heatmap.planning.label'), planningLayers],
        ];

        toggleGroups.forEach(([groupId, groupLabel, entries]) => {
            entries.forEach((entry) => {
                if (!matches(terms, entry.label, groupLabel)) return;

                found.push({
                    key: `toggle:${groupId}:${entry.key}`,
                    label: entry.label,
                    group: groupLabel,
                    active: entry.on,
                    choose: entry.toggle,
                });
            });
        });

        metrics.forEach((metric) => {
            if (metric.kind === 'measure') {
                if (matches(terms, metric.label)) {
                    found.push({
                        key: `metric:${metric.theme}`,
                        label: metric.label,
                        group: String(metric.year ?? ''),
                        active: selected?.theme === metric.theme,
                        choose: () => pick(metric, TOTAL_CATEGORY, metric.categories[0]?.label ?? ''),
                    });
                }
                return;
            }

            metric.categories.forEach((category) => {
                // The theme's name counts as part of the category's: "Zensus
                // Eigentum" has to find a category called just "Eigentümer".
                if (!matches(terms, category.label, metric.label)) return;

                found.push({
                    key: `metric:${metric.theme}:${category.key}`,
                    label: category.label,
                    group: metric.label,
                    active: selected?.theme === metric.theme && selected?.category === category.key,
                    choose: () => pick(metric, category.key, category.label),
                });
            });
        });

        inkarGroups.forEach((group) => {
            const groupLabel = group.label?.[language] ?? group.type;

            group.fields.forEach((indicator) => {
                const label = indicator.label?.[language] ?? indicator.name;
                if (!matches(terms, label, groupLabel)) return;

                found.push({
                    key: `inkar:${group.type}:${indicator.name}`,
                    label,
                    group: groupLabel,
                    active: inkarSelected?.type === group.type && inkarSelected?.field === indicator.name,
                    choose: () => pickInkar(group, indicator),
                });
            });
        });

        POI_GROUPS.forEach((group) => {
            const groupLabel = poiGroupLabel(group.key, i18n.language);

            group.types.forEach((type) => {
                const label = poiTypeLabel(type, i18n.language);
                if (!matches(terms, label, groupLabel)) return;

                found.push({
                    key: `poi:${type.key}`,
                    label,
                    group: groupLabel,
                    active: selectedPoiTypes.includes(type.key),
                    choose: () => onTogglePoiType(type.key),
                });
            });
        });

        return found;
    }

    if (terms.length > 0) {
        return (
            <>
                {searchBox}
                {clearAll}
                <div className={cn(optionList, optionListMetrics)}>
                    {/* A catalogue still loading means the hit list is not final yet;
                        a failed one means it is missing its largest part. */}
                    {catalogueState}
                    {hits.length === 0 && !catalogueState ? (
                        <div className={sectionHint}>{t('heatmap.metrics.noMatch')}</div>
                    ) : (
                        hits.map((hit) => (
                            <button
                                key={hit.key}
                                type="button"
                                aria-pressed={hit.active}
                                className={cn(option, hit.active && optionSelected)}
                                onClick={hit.choose}
                            >
                                <span className={optionBullet} />
                                <span className={optionLabel}>{hit.label}</span>
                                <span className={optionGroup}>{hit.group}</span>
                            </button>
                        ))
                    )}
                </div>
            </>
        );
    }

    /** Ein Zensus-Thema: eine Zeile (Messwert) oder eine Gruppe mit ihren Kategorien. */
    const renderMetric = (metric: MetricTheme) => {
                    const open = openTheme === metric.theme;
                    const measure = metric.kind === 'measure';
                    const themeSelected = selected?.theme === metric.theme;

                    return (
                        <div key={metric.theme}>
                            {measure ? (
                                // A measure has one value per cell, so the row *is* the
                                // choice — no group to open, and the selected chrome
                                // rather than only the weight.
                                <button
                                    type="button"
                                    aria-pressed={themeSelected && selected?.category === TOTAL_CATEGORY}
                                    className={cn(option, themeSelected && optionSelected)}
                                    onClick={() =>
                                        pick(metric, TOTAL_CATEGORY, metric.categories[0]?.label ?? '')
                                    }
                                >
                                    {groupIcon(`theme:${metric.theme}`)}
                                    <span className={optionLabel}>{metric.label}</span>
                                    <span className={optionMeta}>{metric.year}</span>
                                </button>
                            ) : (
                                <GroupHeader
                                    id={`theme:${metric.theme}`}
                                    label={metric.label}
                                    chosen={themeSelected}
                                    meta={metric.year}
                                    open={open}
                                    onOpen={(next) => setOpenTheme(next === null ? null : metric.theme)}
                                />
                            )}

                            {open && !measure ? (
                                <div className={subList}>
                                    {metric.categories.map((category) => {
                                        const active =
                                            selected?.theme === metric.theme &&
                                            selected?.category === category.key;
                                        return (
                                            <button
                                                key={category.key}
                                                type="button"
                                                aria-pressed={active}
                                                className={cn(subOption, active && optionSelected)}
                                                onClick={() => pick(metric, category.key, category.label)}
                                            >
                                                {category.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    );
                };

    /** Eine INKAR-Tabelle mit ihren Indikatoren — einer zugleich, über den Verwaltungseinheiten gezeichnet. */
    const renderInkar = (group: InkarGroup) => {
                    const open = openTheme === `inkar:${group.type}`;

                    return (
                        <div key={`inkar:${group.type}`}>
                            <GroupHeader
                                id={`inkar:${group.type}`}
                                label={group.label?.[language] ?? group.type}
                                chosen={inkarSelected?.type === group.type}
                                open={open}
                                onOpen={setOpenTheme}
                            />

                            {open ? (
                                <div className={subList}>
                                    {group.fields.map((indicator) => {
                                        const active =
                                            inkarSelected?.type === group.type &&
                                            inkarSelected?.field === indicator.name;

                                        return (
                                            <button
                                                key={indicator.name}
                                                type="button"
                                                aria-pressed={active}
                                                className={cn(subOption, active && optionSelected)}
                                                onClick={() => pickInkar(group, indicator)}
                                            >
                                                {indicator.label?.[language] ?? indicator.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    );
                };

    /**
     * Jeder Eintrag der Liste als Baustein mit Kategorie — Zensus-Themen, INKAR-Tabellen, POI-Gruppen und Kartenebenen
     * gemischt, sortiert nach Inhalt statt nach Quelle (categories.ts).
     */
    const blocks: { id: string; category: CategoryKey; chosen: number; node: ReactNode }[] = [];
    const toggleBlock = (id: string, label: string, entries: ToggleEntry[]) =>
        blocks.push({
            id,
            category: fixedCategory(id),
            chosen: entries.filter((entry) => entry.on).length,
            node: <ToggleGroup id={id} label={label} entries={entries} open={openTheme === id} onOpen={setOpenTheme} />,
        });

    blocks.push({ id: 'layers', category: fixedCategory('layers'), chosen: areaLayers.filter((entry) => entry.on).length, node: layerGroup });
    toggleBlock('planning', t('heatmap.planning.label'), planningLayers);

    metrics.forEach((metric) => {
        blocks.push({
            id: `theme:${metric.theme}`,
            category: zensusCategory(metric.theme),
            chosen: selected?.theme === metric.theme ? 1 : 0,
            node: renderMetric(metric),
        });
    });
    inkarGroups.forEach((group) => {
        blocks.push({
            id: `inkar:${group.type}`,
            category: inkarCategory(group.type),
            chosen: inkarSelected?.type === group.type ? 1 : 0,
            node: renderInkar(group),
        });
    });

    toggleBlock('power', t('heatmap.power.label'), powerLayers);
    toggleBlock('ways', t('heatmap.ways.label'), wayLayers);
    toggleBlock('bridges', t('heatmap.bridges.label'), bridgeLayers);
    toggleBlock('hazards', t('heatmap.hazards.label'), hazardLayers);
    toggleBlock('climate', t('heatmap.climate.label'), climateLayers);
    toggleBlock('nature', t('heatmap.nature.label'), natureLayers);

    POI_GROUPS.forEach((group) => {
        const entries = group.types.map((type) => ({
            key: type.key,
            label: poiTypeLabel(type, i18n.language),
            on: selectedPoiTypes.includes(type.key),
            toggle: () => onTogglePoiType(type.key),
        }));
        blocks.push({
            id: `poi:${group.key}`,
            category: poiCategory(group.key),
            chosen: entries.filter((entry) => entry.on).length,
            node: (
                <ToggleGroup
                    id={`poi:${group.key}`}
                    label={poiGroupLabel(group.key, i18n.language)}
                    entries={entries}
                    open={openTheme === `poi:${group.key}`}
                    onOpen={setOpenTheme}
                />
            ),
        });
    });

    return (
        <>
            {searchBox}
            {clearAll}
            <div className={cn(optionList, optionListMetrics)}>
                {catalogueState}

                {CATEGORY_ORDER.map((category) => {
                    const items = blocks.filter((block) => block.category === category);
                    if (items.length === 0) return null;
                    const open = openCategory === category;
                    const chosen = items.reduce((sum, block) => sum + block.chosen, 0);
                    return (
                        <div key={`category:${category}`}>
                            <button
                                type="button"
                                aria-expanded={open}
                                className={cn(option, chosen > 0 && optionMarked)}
                                onClick={() => setOpenCategory(open ? null : category)}
                            >
                                {categoryIcon(category)}
                                <span className={optionLabel}>{t(`heatmap.categories.${category}`)}</span>
                                <span className={optionMeta}>{chosen || ''}</span>
                                {open ? (
                                    <ChevronDown size={13} strokeWidth={2} className={optionChevron} />
                                ) : (
                                    <ChevronRight size={13} strokeWidth={2} className={optionChevron} />
                                )}
                            </button>
                            {open ? (
                                <div className={categoryList}>
                                    {items.map((block) => (
                                        <Fragment key={block.id}>{block.node}</Fragment>
                                    ))}
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
}
